
/**
 * Module dependencies.
 */

import { ProductProps } from 'src/types/products';
import { assetUrlResolve } from 'src/core/utils/url-resolver';
import { color, units } from 'src/styles/utils';
import { finishesValues } from 'src/core/collections-config/finishes';
import { getKeyFromObject } from 'src/core/utils/objects';
import { getProductFeaturedImage } from 'src/core/utils/images';
import { mediasProductName } from 'src/core/collections-config/media';
import { theme } from 'styled-tools';
import { useComparator } from 'src/context/comparator/context';
import { useTranslate } from 'src/core/utils/translator';
import CardTitle from 'src/components/core/typography/card-title';
import FavoritesMiddleman, {
  FavoriteState,
  favoriteStates
} from 'src/components/my-revigres/favorites/favorites-middleman';

import IconButton from 'src/components/core/buttons/icon-button';
import Image, { ImageProps } from 'src/components/core/image';
import ImageDownloadButton from 'src/components/core/buttons/image-download-button';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import RouterLink from 'src/components/core/links/router-link';
import Type from 'src/components/core/typography/index';
import compareAddIcon from 'src/assets/svg/compare-add.svg';
import compareIcon from 'src/assets/svg/compare.svg';
import favoriteAddIcon from 'src/assets/svg/favorite-add.svg';
import favoriteIcon from 'src/assets/svg/favorite.svg';
import filter from 'lodash/filter';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import size from 'lodash/size';
import styled from 'styled-components';
import useBreakpoint from 'src/hooks/use-breakpoint';

/**
 * `Props` type.
 */

type Props = ProductProps & {
  className?: string,
  hasRoomvo?: boolean,
  isKioskCard?: boolean,
  onUpdateList?: () => void,
  url: string
};

/**
 * Roomvo legacy class names.
 *
 * The Roomvo script (`revigrespt.js`, maintained by Roomvo and loaded in
 * `_document.tsx`) finds the product cards by the styled-components classes
 * generated by the production build at the time of the integration. Those
 * classes change whenever this card's CSS changes, so they are pinned here
 * until Roomvo switches to the stable `data-roomvo` attribute — remove them
 * once it does.
 */

const roomvoLegacyClassName = 'sc-ec557735-1 dTOSoF sc-60ea06c0-8 nhHJA';

/**
 * `StyledImage` styled component.
 */

const StyledImage = styled(Image)<ImageProps>`
  transition: transform 5s ease-in;
`;

/**
 * `Card` styled component.
 */

const Card = styled.div`
  position: relative;
  width: 100%;
`;

/**
 * `Link` styled component.
 */

const Link = styled(RouterLink)`
  color: ${color('textColor')};
  position: relative;
  transition: color ${theme('animations.defaultTransition')};
  width: 100%;

  &:focus,
  &:hover {
    color: ${color('brown400')};

    ${StyledImage} {
      transform: scale(1.5);
    }
  }
`;

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  padding-bottom: 100%;
  position: relative;
  width: 100%;
`;

/**
 * `ImageActions` styled component.
 *
 * Sits over the image, outside the card link, so the buttons are not nested
 * inside an anchor. Figma: bottom-right corner, 16px from the edges.
 */

const ImageActions = styled.div`
  left: 0;
  padding-bottom: 100%;
  pointer-events: none;
  position: absolute;
  top: 0;
  width: 100%;

  > * {
    bottom: ${units(2)};
    pointer-events: auto;
    position: absolute;
    right: ${units(2)};
  }
`;

/**
 * `ContentWrapper` styled component.
 */

const ContentWrapper = styled.div`
  padding: ${units(1)};
`;

/**
 * `Title` styled component.
 */

const Title = styled(CardTitle).attrs({ as: 'h3', size: 'small' })`
  margin-bottom: ${units(0.5)};
`;

/**
 * `Format` styled component.
 */

const Format = styled(Type.Small).attrs({
  as: 'div',
  small: true
})`
  color: ${color('brown400')};
`;

/**
 * `Finish` styled component.
 */

const Finish = styled(Format)`
  text-transform: uppercase;
`;

/**
 * `Actions` styled component.
 */

const Actions = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  padding: ${units(1)};

  > button:not(:last-child) {
    margin-right: ${units(2.5)};
  }
`;

/**
 * `ProductCard` component.
 */

const ProductCard = (props: Props): ReactElement => {
  const {
    className,
    finish,
    format,
    hasRoomvo,
    id,
    isKioskCard,
    media,
    name,
    onUpdateList,
    slug,
    url
  } = props;

  const { locale, translate } = useTranslate();
  const imageUrl = useMemo(() => {
    const imageUrl = getProductFeaturedImage(media);

    return imageUrl ?? assetUrlResolve('/static/images/empty/list@2x.jpg');
  }, [media]);

  // `getProductFeaturedImage` falls back to a local placeholder, which must
  // not be offered as a download.
  const downloadImageUrl = useMemo<string | undefined>(() => {
    return get(head(filter(media, { collectionName: mediasProductName.featuredImages })), 'fileUrl');
  }, [media]);

  const alt = get(head(media), 'customProperties.title');
  const finishKey = getKeyFromObject(finishesValues, finish);
  const formatApproximatedValue = useMemo(() => {
    return format?.approximated ? `[ ${format.approximated.split('.').join(',')} cm ]` : '';
  }, [format]);

  const [isFavorite, setIsFavorite] = useState<FavoriteState | null>(null);
  const [isFavoriteTriggered, setIsFavoriteTriggered] = useState<boolean>();
  const [isCompare, setIsCompare] = useState<boolean>();
  const isMobile = useBreakpoint('md', 'max');
  const { onAdd, onRemove, products } = useComparator();
  const isInComparator = useMemo<boolean>(() => {
    return !isEmpty(filter(products, product => product.id === id));
  }, [id, products]);

  const isDisabled = useMemo<boolean>(() => {
    return size(products) === (isMobile ? 2 : 3) && !isInComparator;
  }, [isInComparator, isMobile, products]);

  const handleToggleCompare = useCallback(() => {
    setIsCompare(!isCompare);

    if (isCompare) {
      onRemove(id);

      return;
    }

    onAdd({
      finish: finishKey,
      format,
      formatApproximatedValue,
      id,
      imageUrl,
      name,
      slug
    });
  }, [
    finishKey,
    format,
    formatApproximatedValue,
    id,
    imageUrl,
    isCompare,
    name,
    onAdd,
    onRemove,
    slug
  ]);

  useEffect(() => {
    setIsCompare(isInComparator);
  }, [isInComparator]);

  return (
    <>
      <Card
        className={hasRoomvo ? `${className ?? ''} ${roomvoLegacyClassName}`.trim() : className}
        data-roomvo={hasRoomvo ? 'product-card' : undefined}
      >
        <Link
          href={url}
          locale={locale}
        >
          <ImageWrapper>
            <StyledImage
              alt={alt ? alt : name}
              layout={'fill'}
              objectFit={'cover'}
              src={imageUrl}
            />
          </ImageWrapper>

          <ContentWrapper>
            <Title>
              {name}
            </Title>

            {format && (
              <Format>
                {`${format.name} ${formatApproximatedValue}`}
              </Format>
            )}

            {finishKey && (
              <Finish>
                {translate(`collections:finishes.${finishKey}`)}
              </Finish>
            )}
          </ContentWrapper>
        </Link>

        {!isKioskCard && downloadImageUrl && (
          <ImageActions>
            <ImageDownloadButton url={downloadImageUrl} />
          </ImageActions>
        )}

        <Actions>
          {!isKioskCard && (
            <IconButton
              aria-label={translate(isFavorite === favoriteStates.on ? 'common:actions.removeFromFavorites' : 'common:actions.addToFavorites')}
              disabled={!isFavorite || isFavoriteTriggered}
              icon={isFavorite === favoriteStates.on ? favoriteAddIcon : favoriteIcon}
              iconSize={units(4)}
              label={translate('common:cardActions.save')}
              onClick={() => setIsFavoriteTriggered(true)}
            />
          )}

          <IconButton
            aria-label={translate(isCompare ? 'common:actions.removeFromCompare' : 'common:actions.addToCompare')}
            disabled={isDisabled}
            icon={isCompare ? compareAddIcon : compareIcon}
            iconSize={units(4)}
            label={translate('common:cardActions.productsComparison')}
            onClick={handleToggleCompare}
          />
        </Actions>
      </Card>

      {!isKioskCard && (
        <FavoritesMiddleman
          isFavorite={isFavorite}
          isFavoriteTriggered={isFavoriteTriggered}
          onChangeFavoriteState={setIsFavorite}
          onChangeFavoriteTriggered={setIsFavoriteTriggered}
          onUpdateList={onUpdateList}
          productId={id}
          productName={name}
        />
      )}
    </>
  );
};

/**
 * Export `ProductCard` component.
 */

export default ProductCard;
